import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LazyImage } from '../hooks/useLazyLoad';
import { Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStockInfo = (product) => {
  if (!product.variants || product.variants.length === 0) {
    return { totalStock: product.in_stock ? 100 : 0, minStock: product.in_stock ? 100 : 0 };
  }
  const stocks = product.variants.map(v => v.stock);
  const totalStock = stocks.reduce((sum, s) => sum + s, 0);
  const minStock = Math.min(...stocks.filter(s => s > 0), Infinity);
  return { totalStock, minStock: totalStock > 0 ? minStock : 0 };
};

export const SimilarProducts = ({ categoryId, currentProductId, title = "You May Also Like" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (categoryId) {
      loadSimilarProducts();
    }
  }, [categoryId, currentProductId]);

  const loadSimilarProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=${categoryId}`);
      // Filter out current product and limit to 4
      const filtered = response.data
        .filter(p => p.id !== currentProductId)
        .slice(0, 4);
      setProducts(filtered);
    } catch (error) {
      console.error('Error loading similar products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || products.length === 0) return null;

  return (
    <section className="py-12 border-t border-gray-200" data-testid="similar-products">
      <h2 className="heading-font text-2xl sm:text-3xl font-bold uppercase mb-8 text-[#1A1A1A]">
        {title}
      </h2>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {products.map((product) => {
          const { totalStock, minStock } = getStockInfo(product);
          const isOutOfStock = totalStock === 0;
          const isLowStock = totalStock > 0 && minStock < 3;

          return (
            <Link
              key={product.id}
              to={`/product/${product.id}`}
              className="group block"
              data-testid={`similar-product-${product.id}`}
            >
              <div className="aspect-[3/4] overflow-hidden bg-[#F5F5F3] mb-3 relative rounded-lg">
                <LazyImage
                  src={product.images?.[0] || 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=80'}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${isOutOfStock ? 'opacity-60' : ''}`}
                />
                
                {/* Discount Badge */}
                {product.discount_price && !isOutOfStock && (
                  <div className="absolute top-2 right-2 bg-[#C59746] text-white px-2 py-1 text-xs font-bold rounded">
                    {Math.round(((product.price - product.discount_price) / product.price) * 100)}% OFF
                  </div>
                )}

                {/* Out of Stock Badge */}
                {isOutOfStock && (
                  <div className="absolute top-2 left-2 bg-[#EF4444] text-white px-2.5 py-1 text-xs font-bold rounded">
                    Out of Stock
                  </div>
                )}

                {/* Low Stock Badge */}
                {isLowStock && !isOutOfStock && (
                  <div className="absolute top-2 left-2 bg-[#F97316] text-white px-2.5 py-1 text-xs font-bold rounded flex items-center gap-1">
                    <Flame className="w-3 h-3" />
                    Only {minStock} Left
                  </div>
                )}
              </div>

              <h3 className="text-sm font-semibold mb-2 text-[#1A1A1A] group-hover:text-[#C59746] transition-colors line-clamp-2">
                {product.name}
              </h3>

              <div className="flex items-center space-x-2">
                {product.discount_price ? (
                  <>
                    <span className="text-base font-bold text-[#C59746]">₹{product.discount_price}</span>
                    <span className="text-sm line-through text-gray-400">₹{product.price}</span>
                  </>
                ) : (
                  <span className="text-base font-bold text-[#C59746]">₹{product.price}</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default SimilarProducts;
